"use client";

import { Clock } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

interface WithdrawDnmCardProps {
  /**
   * "active"     — withdraw button enabled (user has DNM to withdraw)
   * "withdrawn"  — green pill, already withdrawn
   * "deposited"  — yellow pill, DNM still deposited (voting period not yet ended)
   */
  state: "active" | "withdrawn" | "deposited";
  onWithdraw?: () => void;
  isPending?: boolean;
}

export const WithdrawDnmCard: React.FC<WithdrawDnmCardProps> = ({
  state,
  onWithdraw,
  isPending,
}) => {
  const t = useTranslations("dao");
  return (
    <div className="flex w-full flex-col items-center justify-center gap-2.5 rounded-md border border-stroke-light px-4 py-3">
      <p
        dir="rtl"
        className="text-center text-xs font-medium leading-[1.4] text-text-primary"
      >
        {t("election.withdraw.info")}
      </p>

      {state === "active" && (
        <button
          type="button"
          onClick={onWithdraw}
          disabled={isPending}
          aria-label={t("election.withdraw.withdrawAria")}
          className="flex h-8 items-center justify-center gap-2 rounded-xs bg-bg-tertiary text-text-primary px-4 disabled:opacity-60"
        >
          <span className="text-sm font-semibold leading-[1.4]" dir="rtl">
            {t("election.withdraw.withdraw")}
          </span>
          <Clock className="size-5" aria-hidden="true" />
        </button>
      )}

      {state === "withdrawn" && (
        <StatePill
          label={t("election.withdraw.withdrawn")}
          className="bg-[rgba(0,166,106,0.2)] text-[#00a66a]"
        />
      )}

      {state === "deposited" && (
        <StatePill
          label={t("election.withdraw.deposited")}
          className="bg-[rgba(255,235,0,0.2)] text-accent"
        />
      )}
    </div>
  );
};

interface StatePillProps {
  label: string;
  className?: string;
}

const StatePill: React.FC<StatePillProps> = ({ label, className }) => (
  <div
    className={cn(
      "flex h-6 w-[108px] items-center justify-center rounded-xs px-2",
      className,
    )}
  >
    <span className="text-xs font-medium leading-[1.4]" dir="rtl">
      {label}
    </span>
  </div>
);
